import React, { useCallback, useEffect, useMemo, useState } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { formatTimeAgo } from "views_shared/app/javascript/utils/dateFormatters";
import OutletRating from "../shared/OutletRating";
import SearchableTable from "../shared/SearchableTable";
import { createPostgRESTDataAdapter } from "../shared/dataAdapters";
import DashboardStats from "./DashboardStats";

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;
const METABASE_URL = () => globalThis.QwotedAdmin?.METABASE_COVERAGE_DASHBOARD_URL;

function formatEnum(value) {
  if (!value) return "";
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const TABS = [
  { key: "manual", label: "Manual Review" },
  { key: "edited", label: "Edited" },
  { key: "intro", label: "Intro" },
  { key: "approved", label: "Approved" },
  { key: "ghosted", label: "Ghosted" },
  { key: "all", label: "All" },
];

const VALID_TAB_KEYS = new Set(TABS.map((tab) => tab.key));

const TAB_CONFIGS = {
  manual: {
    staticFilters: {
      needs_manual_approval_at: "not.is.null",
      approved_at: "is.null",
      ghosted_at: "is.null",
    },
    defaultSort: { needs_manual_approval_at: "asc" },
    columns: {
      id: { type: "number", hidden: true },
      title: { type: "string", display_as: "Request" },
      profile_name: { type: "string", hidden: true },
      profile_image_url: { type: "string", hidden: true },
      media_outlet_name: { type: "string", display_as: "Outlet" },
      media_outlet_prestige: { type: "number", hidden: true },
      media_outlet_domain_authority: { type: "number", hidden: true },
      media_outlet_classification: { type: "string", hidden: true },
      media_outlet_profile_image_url: { type: "string", hidden: true },
      automation_reason: { type: "string", display_as: "Why manual?" },
      request_type: { type: "string", display_as: "Type" },
      request_sub_type: { type: "string", hidden: true },
      needs_manual_approval_at: { type: "datetime", display_as: "Age" },
      free_to_pitch: { type: "boolean", hidden: true },
      paid: { type: "boolean", hidden: true },
      is_first_request: { type: "boolean", hidden: true },
      assigned_to_id: { type: "string", hidden: true },
      assigned_to_name: { type: "string", hidden: true },
      assigned_at: { type: "datetime", hidden: true },
    },
  },
  edited: {
    staticFilters: {
      needs_post_edit_review_at: "not.is.null",
      approved_at: "not.is.null",
      ghosted_at: "is.null",
    },
    defaultSort: { needs_post_edit_review_at: "asc" },
    columns: {
      id: { type: "number", hidden: true },
      title: { type: "string", display_as: "Request" },
      profile_name: { type: "string", hidden: true },
      profile_image_url: { type: "string", hidden: true },
      media_outlet_name: { type: "string", display_as: "Outlet" },
      media_outlet_prestige: { type: "number", hidden: true },
      media_outlet_domain_authority: { type: "number", hidden: true },
      media_outlet_classification: { type: "string", hidden: true },
      media_outlet_profile_image_url: { type: "string", hidden: true },
      distribution_approved_for_feed_at: { type: "datetime", hidden: true },
      distribution_approved_for_notifications_at: { type: "datetime", hidden: true },
      visibility: { type: "string", display_as: "Status" },
      request_type: { type: "string", display_as: "Type" },
      request_sub_type: { type: "string", hidden: true },
      free_to_pitch: { type: "boolean", hidden: true },
      paid: { type: "boolean", hidden: true },
      needs_post_edit_review_at: { type: "datetime", display_as: "Flagged" },
      is_first_request: { type: "boolean", hidden: true },
      assigned_to_id: { type: "string", hidden: true },
      assigned_to_name: { type: "string", hidden: true },
      assigned_at: { type: "datetime", hidden: true },
    },
  },
  intro: {
    staticFilters: {
      request_type: "eq.intro",
      posted_at: "not.is.null",
      approved_at: "is.null",
      ghosted_at: "is.null",
      needs_manual_approval_at: "not.is.null",
    },
    defaultSort: { posted_at: "desc" },
    columns: {
      id: { type: "number", hidden: true },
      title: { type: "string", display_as: "Request" },
      profile_name: { type: "string", hidden: true },
      profile_image_url: { type: "string", hidden: true },
      media_outlet_name: { type: "string", display_as: "Outlet" },
      media_outlet_prestige: { type: "number", hidden: true },
      media_outlet_domain_authority: { type: "number", hidden: true },
      media_outlet_classification: { type: "string", hidden: true },
      media_outlet_profile_image_url: { type: "string", hidden: true },
      approved_at: { type: "datetime", hidden: true },
      ghosted_at: { type: "datetime", hidden: true },
      needs_manual_approval_at: { type: "datetime", hidden: true },
      distribution_approved_for_feed_at: { type: "datetime", hidden: true },
      distribution_approved_for_notifications_at: { type: "datetime", hidden: true },
      visibility: { type: "string", display_as: "Status" },
      free_to_pitch: { type: "boolean", hidden: true },
      paid: { type: "boolean", hidden: true },
      pitch_count: { type: "number", display_as: "Pitches" },
      posted_at: { type: "datetime", display_as: "Posted" },
      is_first_request: { type: "boolean", hidden: true },
      assigned_to_id: { type: "string", hidden: true },
      assigned_to_name: { type: "string", hidden: true },
      assigned_at: { type: "datetime", hidden: true },
    },
  },
  all: {
    staticFilters: { posted_at: "not.is.null" },
    defaultSort: { posted_at: "desc" },
    columns: {
      id: { type: "number", hidden: true },
      title: { type: "string", display_as: "Request" },
      profile_name: { type: "string", hidden: true },
      profile_image_url: { type: "string", hidden: true },
      media_outlet_name: { type: "string", display_as: "Outlet" },
      media_outlet_prestige: { type: "number", hidden: true },
      media_outlet_domain_authority: { type: "number", hidden: true },
      media_outlet_classification: { type: "string", hidden: true },
      media_outlet_profile_image_url: { type: "string", hidden: true },
      approved_at: { type: "datetime", hidden: true },
      ghosted_at: { type: "datetime", hidden: true },
      needs_manual_approval_at: { type: "datetime", hidden: true },
      distribution_approved_for_feed_at: { type: "datetime", hidden: true },
      distribution_approved_for_notifications_at: { type: "datetime", hidden: true },
      visibility: { type: "string", display_as: "Status" },
      request_type: { type: "string", display_as: "Type" },
      request_sub_type: { type: "string", hidden: true },
      free_to_pitch: { type: "boolean", hidden: true },
      paid: { type: "boolean", hidden: true },
      pitch_count: { type: "number", display_as: "Pitches" },
      posted_at: { type: "datetime", display_as: "Posted" },
      is_first_request: { type: "boolean", hidden: true },
      assigned_to_id: { type: "string", hidden: true },
      assigned_to_name: { type: "string", hidden: true },
      assigned_at: { type: "datetime", hidden: true },
    },
  },
  approved: {
    staticFilters: {
      approved_at: "not.is.null",
      ghosted_at: "is.null",
    },
    defaultSort: { approved_at: "desc" },
    columns: {
      id: { type: "number", hidden: true },
      title: { type: "string", display_as: "Request" },
      profile_name: { type: "string", hidden: true },
      profile_image_url: { type: "string", hidden: true },
      media_outlet_name: { type: "string", display_as: "Outlet" },
      media_outlet_prestige: { type: "number", hidden: true },
      media_outlet_domain_authority: { type: "number", hidden: true },
      media_outlet_classification: { type: "string", hidden: true },
      media_outlet_profile_image_url: { type: "string", hidden: true },
      distribution_approved_for_feed_at: { type: "datetime", hidden: true },
      distribution_approved_for_notifications_at: { type: "datetime", hidden: true },
      needs_manual_approval_at: { type: "datetime", hidden: true },
      visibility: { type: "string", display_as: "Status" },
      request_type: { type: "string", hidden: true },
      request_sub_type: { type: "string", hidden: true },
      free_to_pitch: { type: "boolean", hidden: true },
      paid: { type: "boolean", hidden: true },
      pitch_count: { type: "number", display_as: "Pitches" },
      approved_at: { type: "datetime", display_as: "Approved" },
      is_first_request: { type: "boolean", hidden: true },
      assigned_to_id: { type: "string", hidden: true },
      assigned_to_name: { type: "string", hidden: true },
      assigned_at: { type: "datetime", hidden: true },
    },
  },
  ghosted: {
    staticFilters: { ghosted_at: "not.is.null" },
    defaultSort: { ghosted_at: "desc" },
    columns: {
      id: { type: "number", hidden: true },
      title: { type: "string", display_as: "Request" },
      profile_name: { type: "string", hidden: true },
      profile_image_url: { type: "string", hidden: true },
      media_outlet_name: { type: "string", display_as: "Outlet" },
      media_outlet_prestige: { type: "number", hidden: true },
      media_outlet_domain_authority: { type: "number", hidden: true },
      media_outlet_classification: { type: "string", hidden: true },
      media_outlet_profile_image_url: { type: "string", hidden: true },
      visibility: { type: "string", display_as: "Status" },
      request_type: { type: "string", hidden: true },
      request_sub_type: { type: "string", hidden: true },
      free_to_pitch: { type: "boolean", hidden: true },
      paid: { type: "boolean", hidden: true },
      pitch_count: { type: "number", display_as: "Pitches" },
      ghosted_at: { type: "datetime", display_as: "Ghosted" },
      is_first_request: { type: "boolean", hidden: true },
      assigned_to_id: { type: "string", hidden: true },
      assigned_to_name: { type: "string", hidden: true },
      assigned_at: { type: "datetime", hidden: true },
    },
  },
};

const REASON_FILTER_MAP = {
  content_moderation_failed: "eq.content_moderation_failed",
  manual_review: "in.(outlet_manual_review,profile_manual_review,profile_unvetted)",
  ghost: "in.(outlet_ghost,profile_ghost,profile_disabled)",
  no_config: "eq.outlet_no_config",
  intro_request: "eq.intro_request",
  profile_unvetted: "eq.profile_unvetted",
  profile_disabled: "eq.profile_disabled",
};

// --- URL tab state helpers

function getTabFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get("tab");
  return tab && VALID_TAB_KEYS.has(tab) ? tab : "manual";
}

function setTabInUrl(tab) {
  const url = new URL(window.location);
  url.searchParams.set("tab", tab);
  window.history.replaceState({}, "", url);
}

// --- Formatting helpers

function statusBadge(row) {
  if (row.ghosted_at) return <span className="badge bg-danger">Ghosted</span>;
  if (row.distribution_approved_for_notifications_at) return <span className="badge bg-success">Notified</span>;
  if (row.distribution_approved_for_feed_at) return <span className="badge bg-primary">Feed</span>;
  if (row.approved_at) return <span className="badge bg-secondary">Approved</span>;
  if (row.needs_manual_approval_at) return <span className="badge bg-warning text-dark">Manual</span>;
  if (row.posted_at) return <span className="badge bg-info text-dark">Pending</span>;
  return <span className="badge bg-light text-dark">Draft</span>;
}

// --- Manual tab toolbar

function ManualTabToolbar({ reasonFilter, onReasonFilterChange, sortNewest, onSortToggle }) {
  return (
    <div className="d-flex gap-2 align-items-center">
      <select
        className="form-select form-select-sm"
        style={{ width: "auto" }}
        value={reasonFilter}
        onChange={(event) => onReasonFilterChange(event.target.value)}
      >
        <option value="">All reasons</option>
        <option value="content_moderation_failed">Content moderation failed</option>
        <option value="manual_review">Manual review (outlet/profile)</option>
        <option value="ghost">Ghost (outlet/profile/disabled)</option>
        <option value="no_config">No config</option>
        <option value="intro_request">Intro request</option>
        <option value="profile_unvetted">Unvetted profile</option>
        <option value="profile_disabled">Disabled profile</option>
      </select>

      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onSortToggle}>
        <i className={`fa fa-sort-amount-${sortNewest ? "desc" : "asc"} me-1`} />
        {sortNewest ? "Newest first" : "Oldest first"}
      </button>
    </div>
  );
}

// --- Main component

function RequestsAdminPanel() {
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [dashStats, setDashStats] = useState(null);
  const [statsRefreshTrigger, setStatsRefreshTrigger] = useState(0);

  // Manual tab controls
  const [reasonFilter, setReasonFilter] = useState("");
  const [sortNewest, setSortNewest] = useState(false);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setTabInUrl(tab);
    setStatsRefreshTrigger((prev) => prev + 1);
    // Reset manual-only controls when leaving manual tab
    setReasonFilter("");
    setSortNewest(false);
  }, []);

  // Respond to browser back/forward
  useEffect(() => {
    const onPopState = () => setActiveTab(getTabFromUrl());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const tabConfig = TAB_CONFIGS[activeTab];

  const effectiveStaticFilters = useMemo(() => {
    // Dropdown values map to PostgREST filters matching actual pipeline reason values
    // (e.g. "outlet_manual_review", "profile_ghost" — see RequestApprovalPipeline).
    const filters = { ...tabConfig.staticFilters };
    if (activeTab === "manual" && reasonFilter) {
      filters.automation_reason = REASON_FILTER_MAP[reasonFilter] || `eq.${reasonFilter}`;
    }
    return filters;
  }, [activeTab, tabConfig.staticFilters, reasonFilter]);

  // Sort override for manual tab
  const effectiveDefaultSort = useMemo(() => {
    if (activeTab === "manual") {
      return sortNewest ? { needs_manual_approval_at: "desc" } : { needs_manual_approval_at: "asc" };
    }
    return tabConfig.defaultSort;
  }, [activeTab, sortNewest, tabConfig.defaultSort]);

  const dataAdapter = useMemo(() => {
    const headers = authenticatedHeadersHash();
    return createPostgRESTDataAdapter({
      endpoint: `${SUPABASE_URL()}/rest/v1`,
      resource: "admin_review_queue",
      headers,
      staticFilters: effectiveStaticFilters,
      columns: tabConfig.columns,
      searchColumns: ["title", "profile_name", "media_outlet_name"],
    });
  }, [effectiveStaticFilters, tabConfig.columns]);

  const renderCell = (row, column) => {
    if (column === "title") {
      return (
        <div className="d-flex align-items-center">
          {row.profile_image_url ? (
            <img
              src={row.profile_image_url}
              alt=""
              style={{ width: 32, height: 32, objectFit: "cover", borderRadius: "50%", flexShrink: 0 }}
              className="me-2"
            />
          ) : (
            <div
              className="me-2 bg-light d-flex align-items-center justify-content-center text-muted"
              style={{ width: 32, height: 32, borderRadius: "50%", fontSize: "0.75rem", flexShrink: 0 }}
            >
              <i className="fa fa-user" />
            </div>
          )}
          <div>
            <a href={`/admin/requests/${row.id}`} className="text-decoration-none fw-medium">
              {row.title || "Untitled"}
            </a>
            <div className="text-muted small">
              {row.profile_name}
              {row.is_first_request && (
                <span className="badge ms-1" style={{ fontSize: "0.65em", backgroundColor: "#6f42c1" }}>
                  NEW
                </span>
              )}
              {row.assigned_to_name && (
                <span className="badge bg-info text-dark ms-1" style={{ fontSize: "0.65em" }}>
                  Assigned: {row.assigned_to_name}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (column === "media_outlet_name") {
      return (
        <div>
          {row.media_outlet_profile_image_url && (
            <img
              src={row.media_outlet_profile_image_url}
              alt=""
              style={{ maxWidth: 80, maxHeight: 32, objectFit: "contain" }}
              className="d-block mb-1"
            />
          )}
          <div>
            <span>{row.media_outlet_name || "\u2014"}</span>{" "}
            <OutletRating
              prestige={row.media_outlet_prestige}
              domainAuthority={row.media_outlet_domain_authority}
              outletClassification={row.media_outlet_classification}
            />
          </div>
        </div>
      );
    }

    if (column === "visibility") {
      return (
        <div className="d-flex gap-1 align-items-center flex-wrap">
          {statusBadge(row)}
          {row.free_to_pitch && <span className="badge border border-info text-info">Free</span>}
          {row.paid && (
            <span className="text-warning" title="Fee-based">
              <i className="fa fa-dollar-sign" />
            </span>
          )}
        </div>
      );
    }

    if (column === "needs_post_edit_review_at" && activeTab === "edited") {
      const date = row.needs_post_edit_review_at;
      if (!date) return "\u2014";
      const diffMs = new Date() - new Date(date);
      const diffMin = Math.floor(diffMs / 60000);
      const isOld = diffMin > 60;
      return <span className={isOld ? "text-danger fw-bold" : ""}>{formatTimeAgo(date)}</span>;
    }

    if (column === "needs_manual_approval_at" && activeTab === "manual") {
      const date = row.needs_manual_approval_at;
      if (!date) return "\u2014";
      const diffMs = new Date() - new Date(date);
      const diffMin = Math.floor(diffMs / 60000);
      const isOld = diffMin > 60;
      return <span className={isOld ? "text-danger fw-bold" : ""}>{formatTimeAgo(date)}</span>;
    }

    if (column === "request_type") {
      if (!row.request_type) return "\u2014";
      return (
        <div>
          <span className="badge bg-secondary">{formatEnum(row.request_type)}</span>
          {row.request_sub_type && (
            <span className="badge bg-light text-dark border ms-1">{formatEnum(row.request_sub_type)}</span>
          )}
        </div>
      );
    }

    if (column === "pitch_count") {
      return row.pitch_count || "\u2014";
    }

    return undefined;
  };

  const metabaseUrl = METABASE_URL();

  const toolbar =
    activeTab === "manual" ? (
      <ManualTabToolbar
        reasonFilter={reasonFilter}
        onReasonFilterChange={setReasonFilter}
        sortNewest={sortNewest}
        onSortToggle={() => setSortNewest((prev) => !prev)}
      />
    ) : null;

  return (
    <div>
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">Requests</h1>
        {metabaseUrl && (
          <a href={metabaseUrl} target="_blank" rel="noopener noreferrer" className="text-muted small">
            Coverage Dashboard <i className="fa fa-external-link ms-1" />
          </a>
        )}
      </div>

      {/* Dashboard stats */}
      <DashboardStats onStatsLoaded={setDashStats} refreshTrigger={statsRefreshTrigger} />

      {/* Tab navigation */}
      <ul className="nav nav-tabs mb-0">
        {TABS.map((tab) => {
          let badgeCount = null;
          if (tab.key === "manual" && dashStats?.manual_queue_depth) {
            badgeCount = dashStats.manual_queue_depth;
          } else if (tab.key === "edited" && dashStats?.edited_queue_depth) {
            badgeCount = dashStats.edited_queue_depth;
          }

          return (
            <li className="nav-item" key={tab.key}>
              <button
                type="button"
                className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
                {badgeCount != null && badgeCount > 0 && <span className="badge bg-primary ms-2">{badgeCount}</span>}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Table */}
      <SearchableTable
        columns={tabConfig.columns}
        searchColumns={["title", "profile_name", "media_outlet_name"]}
        dataAdapter={dataAdapter}
        pageSize={25}
        defaultSort={effectiveDefaultSort}
        renderCell={renderCell}
        enableFiltering={false}
        toolbar={toolbar}
      />
    </div>
  );
}

export default RequestsAdminPanel;
